import * as environment from '../../environment';
import { v4 } from 'uuid';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Fade, Paper } from '@mui/material';

import {
    clearMessages,
    selectMessages,
    selectModel,
    selectSuggestions,
    selectUserInput,
    setSuggestions,
    setUserInput,
    upsertMessage,
    selectProcessing,
    selectUseTools,
    selectUseSuggestions,
    selectUseAdminTools
} from '../../state/chatSlice';

import {
    connect as socketConnect,
    disconnect as socketDisconnect,
    sendMessage as socketSendMessage
} from '../../state/socketSlice';

import { useAuth } from '../../hooks/useAuth';
import { getRandomSetOfStarterPrompts } from './constants';

import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import UserInput from './UserInput';

const ChatShell = () => {
    const { userDetails } = useAuth();
    const dispatch = useDispatch();
    const messages = useSelector(selectMessages);
    const model = useSelector(selectModel);
    const userInput = useSelector(selectUserInput);
    const suggestions = useSelector(selectSuggestions);
    const processing = useSelector(selectProcessing);
    const useTools = useSelector(selectUseTools);
    const useSuggestions = useSelector(selectUseSuggestions);
    const useAdminTools = useSelector(selectUseAdminTools);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem("jwt");
        dispatch(socketConnect(`${environment.default.wsGatewayBaseUrl}/chat/1?token=${token}`));
        return () => {
            dispatch(socketDisconnect());
        };
    }, [dispatch]);

    useEffect(() => {
        if (userDetails) {
            setIsAdmin(userDetails().role === 'admin');
        }
    }, [userDetails]);

    const sendMessage = useCallback((value) => {
        const newMessage = { id: v4(), content: value, role: 'user' };
        const waitingMessage = { id: v4(), content: '', role: 'assistant' };
        dispatch(upsertMessage(newMessage));
        dispatch(upsertMessage(waitingMessage));
        dispatch(socketSendMessage(
            {
                metadata: {
                    model: model,
                    newMessageId: waitingMessage.id,
                    useTools: useTools,
                    useSuggestions: useSuggestions,
                    useAdminTools: useAdminTools,
                },
                payload: [...messages, newMessage]
            }
        ));
        dispatch(setUserInput(''));
        dispatch(setSuggestions([]));
    }, [dispatch, model, messages, useTools, useSuggestions, useAdminTools]);


    const handleClick = useCallback(() => {
        if (!userInput.trim()) return;
        sendMessage(userInput);
    }, [userInput, sendMessage]);

    const handleSuggestionClick = useCallback((suggestion) => {
        sendMessage(suggestion.value);
    }, [sendMessage]);

    return (
        <Fade in={true} timeout={500}>
            <Container
                maxWidth="xl"
                disableGutters
                sx={{
                    height: 'var(--app-height)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 0,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'var(--app-height)',
                        boxSizing: 'border-box',
                    }}
                >
                    <ChatHeader
                        isAdmin={isAdmin}
                        clearDisabled={processing}
                        onClear={() => {
                            dispatch(setUserInput(''));
                            dispatch(clearMessages());
                            dispatch(setSuggestions(getRandomSetOfStarterPrompts()));
                        }} />
                    <MessageList
                        messages={messages}
                        suggestions={suggestions}
                        onSuggestionSelected={handleSuggestionClick}
                    />
                    <UserInput
                        sendDisabled={processing}
                        value={userInput}
                        onChange={(value) => dispatch(setUserInput(value))}
                        onSend={handleClick}
                    ></UserInput>
                </Paper>
            </Container>
        </Fade>
    );
};

export default ChatShell;;
